import {
	CommentOutlined,
	DeleteOutlined,
	EditOutlined,
	ExclamationCircleOutlined,
	EyeInvisibleOutlined,
	EyeOutlined,
	StopOutlined,
} from "@ant-design/icons";
// import { formatPrice } from "@di-devs/utils-fe";
import {
	Button,
	Divider,
	Modal,
	message,
	Progress,
	Table,
	Tabs,
	Tag,
	Tooltip,
} from "antd";
import type React from "react";
import { useState } from "react";
import {
	useRecordAttendance,
	useUpdateRegistrationStatus,
} from "../hooks/useEventDetail";
import type { EventOccurrence, Registration } from "../types";
import { getRegistrationDisplayName, toJSTDayjs } from "../utils";
import { CapacityEditModal } from "./CapacityEditModal";
import { ChatModal } from "./ChatModal";
import { MemberDetailModal } from "./MemberDetailModal";
import { RegistrationEditModal } from "./RegistrationEditModal";

interface OccurrenceDetailModalProps {
	open: boolean;
	onClose: () => void;
	occurrence?: EventOccurrence & {
		registrations?: Registration[];
		cancels?: Registration[];
		isDisplayed?: boolean;
		cancelCount?: number;
	};
	onUpdateVisibility?: (occurrenceId: number, isDisplayed: boolean) => void;
	onDelete?: (occurrenceId: number) => void;
	onCancelRegistration?: (registrationId: number) => void;
	primaryColor?: string;
	primaryLightColor?: string;
	onRefresh?: () => void;
}

// const styleExpected = {
// 	border: "1px solid #21acd7",
// 	color: "#21acd7",
// 	backgroundColor: "#f0feff",
// 	marginRight: 0,
// };

// const styleAttended = {
// 	border: "1px solid #52c41a",
// 	color: "#52c41a",
// 	backgroundColor: "#f6ffed",
// 	marginRight: 0,
// };

export const OccurrenceDetailModal: React.FC<OccurrenceDetailModalProps> = ({
	open,
	onClose,
	occurrence,
	onUpdateVisibility,
	onDelete,
	onCancelRegistration,
	primaryColor = "#99CA29",
	primaryLightColor = "#fdfff0",
	onRefresh,
}) => {
	const [activeTab, setActiveTab] = useState<string>("registered");
	const [isMemberDetailModalOpen, setIsMemberDetailModalOpen] = useState(false);
	const [isChatModalOpen, setIsChatModalOpen] = useState(false);
	const [isRegistrationEditModalOpen, setIsRegistrationEditModalOpen] =
		useState(false);
	const [isCapacityEditModalOpen, setIsCapacityEditModalOpen] = useState(false);
	const [currentRegistration, setCurrentRegistration] = useState<
		Registration | undefined
	>();
	const [currentMemberId, setCurrentMemberId] = useState<number | undefined>();

	const { mutateAsync: updateRegistrationStatus } =
		useUpdateRegistrationStatus();
	const { mutateAsync: recordAttendance } = useRecordAttendance();

	const handleManualCheckIn = (registration: Registration) => {
		Modal.confirm({
			title: "チェックインの確認",
			content: `${getRegistrationDisplayName(registration)} 様を「完了」にしますか？`,
			okText: "完了",
			cancelText: "キャンセル",
			onOk: async () => {
				try {
					await recordAttendance({
						registrationId: registration.registrationId,
						payload: {
							primaryParticipantCount: 1,
							companionCount: 0,
							checkInSource: "manual",
						},
					});
					message.success("チェックインしました。");
				} catch (error) {
					console.error("Manual check-in error:", error);
					message.error("チェックインに失敗しました。");
				}
			},
		});
	};

	// const checkRegistrationStyle = (registration: Registration) => {
	// 	if ((registration.attended || 0) === 0) {
	// 		return styleExpected;
	// 	}
	// 	return styleAttended;
	// };

	const handleVisibilityUpdate = (isDisplayed: boolean) => {
		if (occurrence && onUpdateVisibility) {
			onUpdateVisibility(occurrence.occurrenceId, isDisplayed);
		}
	};

	const handleDelete = () => {
		if (!occurrence) return;

		Modal.confirm({
			title: "確認",
			icon: <ExclamationCircleOutlined style={{ color: "#dc2626" }} />,
			content: (
				<p>
					<span style={{ color: "#dc2626" }}>
						{occurrence.startAt
							? toJSTDayjs(occurrence.startAt).format("YYYY年M月D日 HH:mm")
							: ""}
					</span>
					の時間を削除してもよろしいでしょうか？
				</p>
			),
			okText: "削除",
			okButtonProps: { size: "large", type: "primary", danger: true },
			cancelText: "閉じる",
			cancelButtonProps: { size: "large" },
			centered: true,
			onOk: () => {
				onDelete?.(occurrence.occurrenceId);
			},
		});
	};

	const handleCancelRegistration = (registration: Registration) => {
		const name = getRegistrationDisplayName(registration);

		Modal.confirm({
			title: "確認",
			icon: <ExclamationCircleOutlined style={{ color: "#dc2626" }} />,
			content: (
				<p>
					<span style={{ color: "#dc2626" }}>{name}</span>
					様の予約をキャンセルしてもよろしいでしょうか？
				</p>
			),
			okText: "キャンセル",
			okButtonProps: { size: "large", type: "primary", danger: true },
			cancelText: "閉じる",
			cancelButtonProps: { size: "large" },
			centered: true,
			onOk: () => {
				onCancelRegistration?.(registration.registrationId);
			},
		});
	};

	const handleUpdateToPaid = (registration: Registration) => {
		const name = getRegistrationDisplayName(registration);

		Modal.confirm({
			title: "入金確認",
			icon: <ExclamationCircleOutlined style={{ color: "#faad14" }} />,
			content: (
				<p>
					<span style={{ color: primaryColor, fontWeight: "bold" }}>
						{name}
					</span>
					様の支払いを「支払済」に変更しますか？
				</p>
			),
			okText: "支払済にする",
			okButtonProps: {
				size: "large",
				type: "primary",
				style: {
					backgroundColor: primaryColor,
					borderColor: primaryColor,
					color: "#fff",
				},
			},
			cancelText: "閉じる",
			cancelButtonProps: { size: "large" },
			centered: true,
			onOk: () => {
				updateRegistrationStatus({
					registrationId: registration.registrationId,
					payload: {
						status: "confirmed",
						paymentStatus: "completed",
					},
				}).then(() => {
					onRefresh?.();
				});
			},
		});
	};

	const commonColumns = [
		{
			title: "LINE名",
			dataIndex: "registration",
			width: 200,
			align: "center" as const,
			render: (registration: Registration) => {
				if (registration.isManual) {
					return <p>ー</p>;
				}
				return (
					<div>
						<p style={{ color: primaryColor, cursor: "pointer" }}>
							{registration.displayName || "ー"}
						</p>
					</div>
				);
			},
		},
		{
			title: "名前",
			dataIndex: "registration",
			width: 200,
			align: "center" as const,
			render: (registration: Registration) => {
				const kana = [registration.lastNameKana, registration.firstNameKana]
					.filter(Boolean)
					.join(" ");
				const memberName =
					`${registration.lastName || ""} ${registration.firstName || ""}`.trim() ||
					registration.nameFull;
				return (
					<div>
						<p>{memberName || "ー"}</p>
						{kana && (
							<p style={{ fontSize: 12, color: "#9ca3af" }}>（{kana}）</p>
						)}
					</div>
				);
			},
		},
		// {
		// 	title: "予約人数/参加者",
		// 	dataIndex: "registration",
		// 	align: "center" as const,
		// 	width: 120,
		// 	render: (registration: Registration) => (
		// 		<Tag style={checkRegistrationStyle(registration)}>
		// 			{registration.expected || registration.participantCount || 0}/
		// 			{registration.attended || registration.actualParticipantCount || 0}人
		// 		</Tag>
		// 	),
		// },
		{
			title: "登録元",
			dataIndex: "isManual",
			align: "center" as const,
			width: 100,
			render: (_isManual: boolean, record: { registration: Registration }) => {
				const { isManual, memberId } = record.registration;
				let label: string;
				let color: string | undefined;
				if (isManual) {
					label = "手作業";
					color = undefined;
				} else if (memberId) {
					label = "LINE";
					color = primaryColor;
				} else {
					label = "LINE（非会員）";
					color = primaryColor;
				}
				return (
					<Tag color={color} style={{ marginRight: 0 }}>
						{label}
					</Tag>
				);
			},
		},
		{
			title: "メッセージ・備考",
			dataIndex: "registration",
			align: "center" as const,
			width: 150,
			render: (registration: Registration) => (
				<>
					<p
						style={{
							textAlign: "center",
							whiteSpace: "pre-wrap",
							fontSize: 12,
						}}
					>
						{registration.message || "ー"}
					</p>
					{registration.remarks && (
						<div style={{ marginTop: 8 }}>
							<p
								style={{
									fontSize: 12,
									textAlign: "center",
									whiteSpace: "pre-wrap",
									backgroundColor: "#f3f4f6",
									borderRadius: 4,
									padding: 4,
								}}
							>
								{registration.remarks}
							</p>
						</div>
					)}
				</>
			),
		},
	];

	const registeredColumns = [
		...commonColumns,
		{
			title: "予約日付",
			dataIndex: "createdAt",
			align: "center" as const,
			width: 130,
			render: (createdAt: string) => (
				<p style={{ whiteSpace: "pre-wrap" }}>
					{createdAt
						? toJSTDayjs(createdAt).format("YYYY年M月D日\nHH:mm")
						: "ー"}
				</p>
			),
		},
		// {
		// 	title: "金額",
		// 	dataIndex: "paymentAmount",
		// 	align: "center" as const,
		// 	width: 120,
		// 	render: (value: number | undefined) =>
		// 		value ? formatPrice(value) : "ー",
		// },
		// {
		// 	title: "Order ID",
		// 	dataIndex: "paymentOrderId",
		// 	align: "center" as const,
		// 	width: 200,
		// 	render: (value: string | undefined) => value || "ー",
		// },
		{
			title: "決済状態",
			dataIndex: "registration",
			align: "center" as const,
			width: 100,
			render: (registration: Registration) => {
				const isCompleted = registration.paymentStatus === "completed";
				const isFree = registration.paymentStatus === "free";

				if (isFree) {
					return (
						<Tag color="default" style={{ marginRight: 0, cursor: "default" }}>
							無料
						</Tag>
					);
				}

				return (
					<Tag
						color={isCompleted ? "success" : "error"}
						onClick={
							!isCompleted ? () => handleUpdateToPaid(registration) : undefined
						}
						style={{
							cursor: !isCompleted ? "pointer" : "default",
							marginRight: 0,
						}}
					>
						{isCompleted ? "支払済" : "支払前"}
					</Tag>
				);
			},
		},
		{
			title: "チェックイン",
			dataIndex: "registration",
			align: "center" as const,
			width: 100,
			render: (registration: Registration) => {
				const isCheckedIn =
					registration.checkedIn || (registration.attended || 0) > 0;
				if (isCheckedIn) {
					return (
						<Tag color="success" style={{ marginRight: 0 }}>
							完了
						</Tag>
					);
				}

				const now = toJSTDayjs(new Date());
				const eventEnd = occurrence?.endAt
					? toJSTDayjs(occurrence.endAt)
					: null;

				if (eventEnd && now.isAfter(eventEnd)) {
					return (
						<Tag color="default" style={{ marginRight: 0 }}>
							欠席
						</Tag>
					);
				}

				// Manual check-in only for non-member and non-active registrations
				// Active Furukawa members auto-check-in via visit scan — show non-clickable tag
				const canManualCheckIn =
					!registration.memberId || registration.membershipStatus !== "active";

				if (canManualCheckIn) {
					return (
						<Tag
							color="error"
							style={{ marginRight: 0, cursor: "pointer" }}
							onClick={() => handleManualCheckIn(registration)}
						>
							未完了
						</Tag>
					);
				}

				return (
					<Tooltip title="会員は来館スキャンで自動チェックイン">
						<Tag
							color="error"
							style={{ marginRight: 0, opacity: 0.5, cursor: "default" }}
						>
							未完了
						</Tag>
					</Tooltip>
				);
			},
		},
		{
			title: "",
			dataIndex: "action",
			align: "center" as const,
			width: 180,
			render: (_: unknown, record: { registration: Registration }) => (
				<div
					style={{
						display: "flex",
						gap: 4,
						justifyContent: "center",
						flexWrap: "wrap",
					}}
				>
					<Tooltip title="お客様詳細">
						<Button
							icon={<EyeOutlined />}
							onClick={() => {
								setCurrentRegistration(record.registration);
								setCurrentMemberId(record.registration.memberId);
								setIsMemberDetailModalOpen(true);
							}}
						/>
					</Tooltip>
					{(record.registration.isManual || !record.registration.memberId) && (
						<Tooltip title="予約内容編集">
							<Button
								icon={<EditOutlined />}
								onClick={() => {
									setCurrentRegistration(record.registration);
									setIsRegistrationEditModalOpen(true);
								}}
							/>
						</Tooltip>
					)}
					{record.registration.lineUserId && (
						<Tooltip title="LINEチャット">
							<Button
								icon={<CommentOutlined />}
								onClick={() => {
									setCurrentRegistration(record.registration);
									setCurrentMemberId(
										record.registration.memberId ||
											record.registration.chatMemberId,
									);
									setIsChatModalOpen(true);
								}}
							/>
						</Tooltip>
					)}
					{(record.registration.attended || 0) === 0 && (
						<Tooltip title="予約キャンセル">
							<Button
								icon={<StopOutlined />}
								onClick={() => handleCancelRegistration(record.registration)}
								danger
							/>
						</Tooltip>
					)}
				</div>
			),
		},
	];

	const canceledColumns = [
		...commonColumns,
		{
			title: "キャンセル日付",
			dataIndex: "cancelledAt",
			align: "center" as const,
			width: 130,
			render: (cancelledAt: string) => (
				<p style={{ whiteSpace: "pre-wrap" }}>
					{cancelledAt
						? toJSTDayjs(cancelledAt).format("YYYY年M月D日\nHH:mm")
						: "ー"}
				</p>
			),
		},
		{
			title: "",
			dataIndex: "action",
			align: "center" as const,
			width: 120,
			render: (_: unknown, record: { registration: Registration }) => (
				<div style={{ display: "flex", gap: 4, justifyContent: "center" }}>
					<Tooltip title="お客様詳細">
						<Button
							icon={<EyeOutlined />}
							onClick={() => {
								setCurrentRegistration(record.registration);
								setCurrentMemberId(record.registration.memberId);
								setIsMemberDetailModalOpen(true);
							}}
						/>
					</Tooltip>
					{record.registration.lineUserId && (
						<Tooltip title="LINEチャット">
							<Button
								icon={<CommentOutlined />}
								onClick={() => {
									setCurrentRegistration(record.registration);
									setCurrentMemberId(
										record.registration.memberId ||
											record.registration.chatMemberId,
									);
									setIsChatModalOpen(true);
								}}
							/>
						</Tooltip>
					)}
				</div>
			),
		},
	];

	if (!occurrence) {
		return null;
	}

	const reservationRate =
		occurrence.maxAttendees > 0
			? Math.round((occurrence.sumExpected / occurrence.maxAttendees) * 100)
			: 0;

	const attendanceRate =
		occurrence.sumExpected > 0
			? Math.round((occurrence.sumAttended / occurrence.sumExpected) * 100)
			: 0;

	const cancelCount = occurrence.cancelCount || occurrence.cancels?.length || 0;

	return (
		<>
			<Modal
				open={open}
				onCancel={onClose}
				title="時間詳細"
				footer={null}
				width={1280}
				destroyOnClose
				centered
				styles={{
					body: {
						maxHeight: "90vh",
						overflowY: "auto",
						overflowX: "auto",
					},
				}}
			>
				<div style={{ display: "flex", flexDirection: "column" }}>
					{/* Display Status Tag */}
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							marginBottom: 16,
						}}
					>
						{occurrence.isDisplayed !== false ? (
							<Tag
								icon={<EyeOutlined />}
								color={primaryColor}
								style={{ padding: "4px 12px", fontSize: 14 }}
							>
								表示中
							</Tag>
						) : (
							<Tag
								icon={<EyeInvisibleOutlined />}
								color="#ef4444"
								style={{ padding: "4px 12px", fontSize: 14 }}
							>
								非表示中
							</Tag>
						)}
					</div>

					{/* Action Buttons */}
					<div
						style={{
							display: "flex",
							justifyContent: "flex-end",
							marginBottom: 16,
							gap: 8,
						}}
					>
						{occurrence.isDisplayed !== false ? (
							<Button
								type="primary"
								icon={<EyeInvisibleOutlined />}
								onClick={() => handleVisibilityUpdate(false)}
								style={{
									backgroundColor: primaryColor,
									borderColor: primaryColor,
								}}
							>
								非表示する
							</Button>
						) : (
							<Button
								type="primary"
								icon={<EyeOutlined />}
								onClick={() => handleVisibilityUpdate(true)}
								style={{
									backgroundColor: primaryColor,
									borderColor: primaryColor,
								}}
							>
								表示する
							</Button>
						)}
						<Button
							type="primary"
							icon={<EditOutlined />}
							style={{
								backgroundColor: primaryColor,
								borderColor: primaryColor,
							}}
							onClick={() => setIsCapacityEditModalOpen(true)}
						>
							編集
						</Button>
						<Button
							danger
							type="primary"
							icon={<DeleteOutlined />}
							onClick={handleDelete}
						>
							削除
						</Button>
					</div>

					{/* Time Display Card */}
					<div
						style={{
							border: "1px solid #e5e7eb",
							borderRadius: 8,
							padding: 24,
							marginBottom: 16,
						}}
					>
						<div style={{ textAlign: "center", marginBottom: 16 }}>
							<span style={{ fontSize: 24, fontWeight: "bold" }}>
								{occurrence.startAt
									? toJSTDayjs(occurrence.startAt).format("HH:mm")
									: "ー"}
							</span>
							<span style={{ margin: "0 16px", fontSize: 24, color: "#666" }}>
								〜
							</span>
							<span style={{ fontSize: 24, fontWeight: "bold" }}>
								{occurrence.endAt
									? toJSTDayjs(occurrence.endAt).format("HH:mm")
									: "ー"}
							</span>
						</div>

						{/* Stats Row */}
						<div
							style={{
								display: "grid",
								gridTemplateColumns: "1fr 1fr 1fr",
								textAlign: "center",
								borderTop: "1px solid #e5e7eb",
								paddingTop: 16,
							}}
						>
							<div style={{ borderRight: "1px solid #e5e7eb" }}>
								<div style={{ color: "#666", marginBottom: 4 }}>予約数</div>
								<div style={{ fontSize: 20, fontWeight: "bold" }}>
									{occurrence.sumExpected || 0}/
									<span style={{ color: "#999", fontWeight: "normal" }}>
										{occurrence.maxAttendees || 0}
									</span>
								</div>
							</div>
							<div style={{ borderRight: "1px solid #e5e7eb" }}>
								<div style={{ color: "#666", marginBottom: 4 }}>参加数</div>
								<div style={{ fontSize: 20, fontWeight: "bold" }}>
									{occurrence.sumAttended || 0}
								</div>
							</div>
							<div>
								<div style={{ color: "#666", marginBottom: 4 }}>
									キャンセル数
								</div>
								<div style={{ fontSize: 20, fontWeight: "bold" }}>
									{cancelCount}
								</div>
							</div>
						</div>
					</div>

					{/* Progress Circles */}
					<div
						style={{
							display: "flex",
							justifyContent: "center",
							gap: 48,
							marginBottom: 16,
						}}
					>
						<div style={{ textAlign: "center" }}>
							<Progress
								type="circle"
								percent={reservationRate}
								size={100}
								strokeColor={primaryColor}
								trailColor="#f0f0f0"
								format={() => (
									<div>
										<div style={{ fontSize: 12, color: "#666" }}>予約率</div>
										<div style={{ fontSize: 16, fontWeight: "bold" }}>
											{reservationRate}%
										</div>
									</div>
								)}
							/>
						</div>
						<div style={{ textAlign: "center" }}>
							<Progress
								type="circle"
								percent={attendanceRate}
								size={100}
								strokeColor="#22c55e"
								trailColor="#f0f0f0"
								format={() => (
									<div>
										<div style={{ fontSize: 12, color: "#666" }}>参加者率</div>
										<div style={{ fontSize: 16, fontWeight: "bold" }}>
											{attendanceRate}%
										</div>
									</div>
								)}
							/>
						</div>
					</div>

					{/* Remarks */}
					{occurrence.remarks && (
						<div
							style={{
								textAlign: "center",
								whiteSpace: "pre-wrap",
								backgroundColor: "#f3f4f6",
								borderRadius: 24,
								padding: 16,
								marginBottom: 16,
							}}
						>
							{occurrence.remarks}
						</div>
					)}

					<Divider />

					{/* Tabs for Registrations */}
					<Tabs
						activeKey={activeTab}
						onChange={setActiveTab}
						type="card"
						centered
						size="large"
						items={[
							{
								key: "registered",
								label: "予約一覧",
								children: (
									<Table
										columns={registeredColumns}
										dataSource={
											occurrence.registrations?.map((registration) => ({
												key: registration.registrationId,
												registration,
												isManual: registration.isManual,
												createdAt: registration.createdAt,
												paymentAmount: registration.paymentAmount,
												paymentOrderId: registration.paymentOrderId,
											})) || []
										}
										bordered
										size="small"
										scroll={{ x: "max-content" }}
										pagination={{ pageSize: 10 }}
										locale={{ emptyText: "データがありません" }}
									/>
								),
							},
							{
								key: "canceled",
								label: "キャンセル一覧",
								children: (
									<Table
										columns={canceledColumns}
										dataSource={
											occurrence.cancels?.map((registration) => ({
												key: registration.registrationId,
												registration,
												isManual: registration.isManual,
												cancelledAt: registration.cancelledAt,
											})) || []
										}
										bordered
										size="small"
										scroll={{ x: "max-content" }}
										pagination={{ pageSize: 10 }}
										locale={{ emptyText: "データがありません" }}
									/>
								),
							},
						]}
					/>
				</div>
			</Modal>

			<MemberDetailModal
				open={isMemberDetailModalOpen}
				onClose={() => setIsMemberDetailModalOpen(false)}
				memberId={currentMemberId}
				registration={currentRegistration}
				primaryColor={primaryColor}
			/>

			<RegistrationEditModal
				open={isRegistrationEditModalOpen}
				onClose={() => setIsRegistrationEditModalOpen(false)}
				registration={currentRegistration}
				occurrence={occurrence}
				primaryColor={primaryColor}
				primaryLightColor={primaryLightColor}
				onSuccess={() => {
					setIsRegistrationEditModalOpen(false);
					onRefresh?.();
				}}
			/>

			<ChatModal
				open={isChatModalOpen}
				onClose={() => setIsChatModalOpen(false)}
				memberId={currentMemberId as number}
				memberName={getRegistrationDisplayName(currentRegistration)}
				primaryColor={primaryColor}
			/>

			<CapacityEditModal
				open={isCapacityEditModalOpen}
				onClose={() => setIsCapacityEditModalOpen(false)}
				occurrence={occurrence}
				primaryColor={primaryColor}
				onSuccess={() => {
					setIsCapacityEditModalOpen(false);
					onRefresh?.();
				}}
			/>
		</>
	);
};
